import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useOutlet, useSearchParams } from 'react-router';
import { ApiClientError } from '../api/client';
import {
  messagingApi,
  type MailboxBoxes,
  type MailboxEntry,
  type MailboxQuery,
  type MessageCursor,
  type TeamThread,
} from '../api/endpoints';
import { messageFor } from '../api/messages';
import { dateTime } from '../format';
import { onUnreadChanged } from '../hooks/useUnreadMessages';
import { Icon } from './icons';
import { Alert, Button } from './ui';

const PAGE_SIZE = 30;

/** Primärer Knopf „Neue Nachricht“; auf dem Handy nur das Symbol (der Name bleibt für Vorlesegeräte). */
export function NewMessageLink({ to }: { to: string }) {
  return (
    <Link className="btn mailbox__new" to={to} aria-label="Neue Nachricht">
      <Icon name="plus" size={20} />
      <span className="mailbox__new-text">Neue Nachricht</span>
    </Link>
  );
}

type Art = NonNullable<MailboxQuery['kind']>;

const ART_LABELS: Record<Art, string> = {
  direct: 'Gespräche',
  broadcast: 'Rundschreiben',
  org: 'Organisation',
};

function isArt(value: string | null): value is Art {
  return value === 'direct' || value === 'broadcast' || value === 'org';
}

/**
 * Seite des Verlaufs zu einem Eintrag. Im Basar-Postfach (`fixedEventId`) bleiben Team-Fäden unter dem Basar,
 * sonst unter `/nachrichten/basar/…`. Eigene Fäden und Rundschreiben der Organisation haben ihre eigene Seite.
 */
export function entryPath(entry: MailboxEntry, fixedEventId?: string): string {
  if (entry.box === 'org') return `/nachrichten/org/${entry.id}`;
  if (entry.box === 'team' && entry.eventId !== null) {
    return fixedEventId === undefined
      ? `/nachrichten/basar/${entry.eventId}/${entry.id}`
      : `/basare/${fixedEventId}/nachrichten/${entry.id}`;
  }
  return `/nachrichten/${entry.id}`;
}

/** Kennzeichen vor dem Betreff: Basar bzw. „Organisation“. */
function originLabel(entry: MailboxEntry): string {
  return entry.box === 'org' || entry.eventTitle === null
    ? `Organisation ${entry.organizationName}`
    : entry.eventTitle;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Kurze Zeit für die Liste: heute die Uhrzeit, sonst Tag und Monat. Die volle Angabe steht im `title`. */
function shortTime(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const zone = { timeZone: 'Europe/Berlin' } as const;
  const day = (value: Date): string =>
    new Intl.DateTimeFormat('de-DE', { ...zone, dateStyle: 'short' }).format(value);
  if (day(date) === day(now)) {
    return new Intl.DateTimeFormat('de-DE', { ...zone, timeStyle: 'short' }).format(date);
  }
  return new Intl.DateTimeFormat('de-DE', { ...zone, day: '2-digit', month: '2-digit' }).format(
    date,
  );
}

function MailboxRow({
  entry,
  fixedEventId,
  current,
}: {
  entry: MailboxEntry;
  fixedEventId: string | undefined;
  current: boolean;
}) {
  const sent = entry.readCount !== null && entry.recipientCount !== null;
  return (
    <li>
      <Link
        to={entryPath(entry, fixedEventId)}
        className={`mailbox__row${entry.unread ? ' unread' : ''}`}
        aria-current={current ? 'page' : undefined}
      >
        <span className="mailbox__top">
          <span className="mailbox__from">
            {entry.unread && <span className="unread-dot" role="img" aria-label="ungelesen" />}
            <span className="mailbox__ellipsis">{entry.counterpart}</span>
          </span>
          <time
            className="mailbox__time"
            dateTime={entry.lastMessageAt}
            title={dateTime(entry.lastMessageAt)}
          >
            {shortTime(entry.lastMessageAt)}
          </time>
        </span>
        <span className="mailbox__subject">
          {fixedEventId === undefined && (
            <span className="mailbox__origin" title={originLabel(entry)}>
              {originLabel(entry)}
            </span>
          )}
          <span className="mailbox__ellipsis mailbox__subject-text">{entry.subject}</span>
        </span>
        <span className="mailbox__snippet muted">
          {entry.snippet === null ? <em>Nachricht entfernt</em> : entry.snippet}
          {sent && (
            <span className="mailbox__read">
              {' '}
              · gelesen {entry.readCount} von {entry.recipientCount}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}

/** Team-Faden der alten Basar-Route als Eintrag (Plan 16, 4.13.3: Support-Zugriff bleibt über den Basar erreichbar). */
function fromTeamThread(eventId: string, thread: TeamThread): MailboxEntry {
  const person =
    thread.participant === null
      ? 'Unbekannt'
      : [thread.participant.firstName, thread.participant.lastName]
          .filter((part) => part !== null && part !== '')
          .join(' ');
  return {
    id: thread.id,
    box: 'team',
    eventId,
    eventTitle: null,
    organizationId: '',
    organizationName: '',
    kind: thread.kind,
    subject: thread.subject,
    counterpart: thread.kind === 'broadcast' ? `An: ${thread.audienceLabel ?? 'Gruppe'}` : person,
    snippet: null,
    lastMessageAt: thread.lastMessageAt,
    unread: thread.unread,
    canReply: thread.kind === 'direct',
    readCount: thread.kind === 'broadcast' ? thread.readCount : null,
    recipientCount: thread.kind === 'broadcast' ? thread.recipientCount : null,
  };
}

/** Neue Seite vor die schon geladenen setzen; ältere, noch nicht ersetzte Zeilen bleiben, nie doppelt. */
function mergeFresh(
  fresh: MailboxEntry[],
  freshNext: MessageCursor | null,
  existing: MailboxEntry[],
  existingNext: MessageCursor | null,
): { entries: MailboxEntry[]; next: MessageCursor | null } {
  const last = fresh[fresh.length - 1];
  if (freshNext === null || last === undefined) return { entries: fresh, next: freshNext };
  const seen = new Set(fresh.map((entry) => `${entry.box}:${entry.id}`));
  const older = existing.filter(
    (entry) => !seen.has(`${entry.box}:${entry.id}`) && entry.lastMessageAt <= last.lastMessageAt,
  );
  return { entries: [...fresh, ...older], next: older.length > 0 ? existingNext : freshNext };
}

/**
 * Das eine Postfach (Plan 16, 4.13): Fäden mit Team-Recht, eigene Fäden und Rundschreiben der Organisation in einer
 * Liste, neueste zuerst. Filter stehen in der Adresse (`?filter=unread&basar=…&art=…`), damit „Zurück“ sie behält.
 * Mit `eventId` ist der Basar fest gewählt (Basar-Postfach), ohne gibt es die Auswahl.
 */
export function Mailbox({ eventId }: { eventId?: string }) {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const unreadOnly = params.get('filter') === 'unread';
  const artParam = params.get('art');
  const originParam = params.get('basar');
  const orgParam = params.get('org');
  // Kaputte Werte aus der Adresse (keine Kennung) werden verworfen
  const originId = originParam !== null && UUID.test(originParam) ? originParam : undefined;
  const orgId = orgParam !== null && UUID.test(orgParam) ? orgParam : undefined;
  const filterEventId = eventId ?? originId;
  const filterOrgId = filterEventId === undefined ? orgId : undefined;
  // Basar und „Organisation“ schließen sich aus
  const art =
    isArt(artParam) && !(artParam === 'org' && filterEventId !== undefined) ? artParam : null;

  const [boxes, setBoxes] = useState<MailboxBoxes | null>(null);
  const [entries, setEntries] = useState<MailboxEntry[] | null>(null);
  const [next, setNext] = useState<MessageCursor | null>(null);
  const [error, setError] = useState<{ message: string; filter: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  // Läuft eine Abfrage, die inzwischen überholt ist (anderer Filter), wird ihre Antwort verworfen
  const generation = useRef(0);
  const entriesRef = useRef<MailboxEntry[] | null>(null);
  const nextRef = useRef<MessageCursor | null>(null);
  useEffect(() => {
    entriesRef.current = entries;
    nextRef.current = next;
  });
  // Basar-Postfach ohne Postfach-Recht (Support-Zugriff): Liste aus der Team-Route des Basars
  const viaTeam = useRef(false);

  const query = useMemo<MailboxQuery>(() => {
    const result: MailboxQuery = {};
    if (unreadOnly) result.filter = 'unread';
    if (filterEventId !== undefined) result.eventId = filterEventId;
    if (filterOrgId !== undefined) result.orgId = filterOrgId;
    if (art !== null) result.kind = art;
    return result;
  }, [unreadOnly, filterEventId, filterOrgId, art]);
  const filterActive =
    unreadOnly ||
    art !== null ||
    filterOrgId !== undefined ||
    (eventId === undefined && filterEventId !== undefined);

  const fetchPage = useCallback(
    async (
      cursor: MessageCursor | null,
    ): Promise<{ entries: MailboxEntry[]; next: MessageCursor | null }> => {
      const fromTeam = async () => {
        const loaded = await messagingApi.teamThreads(
          eventId ?? '',
          query.kind === 'direct' || query.kind === 'broadcast' ? query.kind : null,
          cursor,
          PAGE_SIZE,
        );
        const mapped = loaded.threads.map((thread) => fromTeamThread(eventId ?? '', thread));
        return {
          entries: query.filter === 'unread' ? mapped.filter((entry) => entry.unread) : mapped,
          next: loaded.next,
        };
      };
      if (eventId !== undefined && viaTeam.current) return fromTeam();
      try {
        return await messagingApi.mailbox(cursor, PAGE_SIZE, query);
      } catch (caught) {
        if (eventId !== undefined && caught instanceof ApiClientError && caught.status === 404) {
          viaTeam.current = true;
          return fromTeam();
        }
        throw caught;
      }
    },
    [eventId, query],
  );

  // Basare und Organisationen für die Auswahl (nur im Gesamt-Postfach); Fehler bleiben still, die Liste geht auch so
  useEffect(() => {
    if (eventId !== undefined) return;
    const guard = { cancelled: false };
    messagingApi.mailboxBoxes().then(
      (loaded) => {
        if (!guard.cancelled) setBoxes(loaded);
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, [eventId]);

  // Nach dem Lesen oder Antworten (Zähler ändert sich) die erste Seite neu holen und einarbeiten
  useEffect(
    () =>
      onUnreadChanged(() => {
        setReload((count) => count + 1);
      }),
    [],
  );

  // Erste Seite bei Filterwechsel (alles neu) und bei `reload` (Einarbeiten, geladene ältere Seiten bleiben)
  const lastQuery = useRef<MailboxQuery | null>(null);
  useEffect(() => {
    const replace = lastQuery.current !== query;
    lastQuery.current = query;
    if (replace) generation.current += 1;
    const mine = generation.current;
    fetchPage(null).then(
      (loaded) => {
        if (mine !== generation.current) return;
        setError(null);
        if (replace) {
          setEntries(loaded.entries);
          setNext(loaded.next);
          return;
        }
        // Einarbeiten, ohne die schon geladenen älteren Seiten zu verlieren
        const merged = mergeFresh(
          loaded.entries,
          loaded.next,
          entriesRef.current ?? [],
          nextRef.current,
        );
        setEntries(merged.entries);
        setNext(merged.next);
      },
      (caught: unknown) => {
        if (mine !== generation.current) return;
        // Fremder oder kaputter Filter (400/404) mit aktivem Filter: Hinweis mit Zurücksetzen
        const stale =
          caught instanceof ApiClientError &&
          (caught.status === 404 || caught.status === 400) &&
          filterActive;
        setError({ message: messageFor(caught), filter: stale });
      },
    );
  }, [fetchPage, query, reload, filterActive]);

  const change = (changes: Record<string, string | null>): void => {
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        for (const [key, value] of Object.entries(changes)) {
          if (value === null) copy.delete(key);
          else copy.set(key, value);
        }
        return copy;
      },
      { replace: true },
    );
    setEntries(null);
    setNext(null);
    setError(null);
  };

  const loadMore = (): void => {
    if (next === null) return;
    const mine = generation.current;
    setBusy(true);
    fetchPage(next).then(
      (loaded) => {
        if (mine !== generation.current) return;
        setEntries((existing) => {
          const seen = new Set((existing ?? []).map((entry) => `${entry.box}:${entry.id}`));
          return [
            ...(existing ?? []),
            ...loaded.entries.filter((entry) => !seen.has(`${entry.box}:${entry.id}`)),
          ];
        });
        setNext(loaded.next);
        setBusy(false);
      },
      (caught: unknown) => {
        if (mine !== generation.current) return;
        setError({ message: messageFor(caught), filter: false });
        setBusy(false);
      },
    );
  };

  // Auswahl „Basar“: team + mine ohne Doppelte, dazu Organisationen mit Rundschreiben
  const origins = useMemo(() => {
    if (boxes === null) return [];
    const events = new Map<string, string>();
    for (const box of boxes.team) events.set(box.eventId, box.eventTitle);
    for (const box of boxes.mine) events.set(box.eventId, box.eventTitle);
    return [
      ...[...events].map(([id, title]) => ({ value: `e:${id}`, label: title })),
      ...boxes.orgs.map((org) => ({
        value: `o:${org.organizationId}`,
        label: `Organisation ${org.organizationName}`,
      })),
    ];
  }, [boxes]);
  const originValue =
    filterEventId !== undefined
      ? `e:${filterEventId}`
      : filterOrgId === undefined
        ? ''
        : `o:${filterOrgId}`;
  const knownOrigin = origins.some((origin) => origin.value === originValue);

  const chooseOrigin = (value: string): void => {
    const dropOrgArt: Record<string, string | null> = art === 'org' ? { art: null } : {};
    if (value === '') change({ basar: null, org: null });
    else if (value.startsWith('e:')) change({ basar: value.slice(2), org: null, ...dropOrgArt });
    else change({ basar: null, org: value.slice(2) });
  };
  const chooseArt = (value: string): void => {
    if (value === 'org') change({ art: 'org', basar: null });
    else change({ art: value === '' ? null : value });
  };

  const filtered = unreadOnly || art !== null || (eventId === undefined && originValue !== '');
  const arts: Art[] =
    eventId === undefined && filterEventId === undefined
      ? ['direct', 'broadcast', 'org']
      : ['direct', 'broadcast'];
  const showOrigin = eventId === undefined && (origins.length > 1 || originValue !== '');

  return (
    <div className="mailbox stack">
      <div className="mailbox__filters">
        <div className="chips" role="group" aria-label="Anzeige">
          <button
            type="button"
            className="chip"
            aria-pressed={!unreadOnly}
            onClick={() => {
              change({ filter: null });
            }}
          >
            Alle
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={unreadOnly}
            onClick={() => {
              change({ filter: 'unread' });
            }}
          >
            Ungelesen
          </button>
        </div>
        <div className="mailbox__selects">
          {showOrigin && (
            <select
              aria-label="Basar"
              value={originValue}
              onChange={(changeEvent) => {
                chooseOrigin(changeEvent.target.value);
              }}
            >
              <option value="">Alle Basare</option>
              {!knownOrigin && originValue !== '' && (
                <option value={originValue}>Gewählter Filter</option>
              )}
              {origins.map((origin) => (
                <option key={origin.value} value={origin.value}>
                  {origin.label}
                </option>
              ))}
            </select>
          )}
          <select
            aria-label="Art"
            value={art ?? ''}
            onChange={(changeEvent) => {
              chooseArt(changeEvent.target.value);
            }}
          >
            <option value="">Alle Arten</option>
            {arts.map((entry) => (
              <option key={entry} value={entry}>
                {ART_LABELS[entry]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {error !== null && (
        <Alert kind="error">
          {error.message}
          {error.filter && (
            <>
              {' '}
              <Button
                icon="filter"
                variant="secondary"
                small
                onClick={() => {
                  change({ filter: null, basar: null, org: null, art: null });
                }}
              >
                Filter zurücksetzen
              </Button>
            </>
          )}
        </Alert>
      )}
      {entries === null ? (
        error === null && <p role="status">Einen Moment bitte …</p>
      ) : entries.length === 0 ? (
        <Alert kind="info">
          {filtered
            ? 'Keine Nachrichten mit diesem Filter.'
            : 'Noch keine Nachrichten. Rundschreiben und Antworten erscheinen hier. '}
          {!filtered && (
            <Link
              to={eventId === undefined ? '/nachrichten/neu' : `/basare/${eventId}/nachrichten/neu`}
            >
              Jetzt schreiben
            </Link>
          )}
        </Alert>
      ) : (
        <ul className="mailbox__list" aria-label="Nachrichten">
          {entries.map((entry) => {
            const path = entryPath(entry, eventId);
            return (
              <MailboxRow
                key={`${entry.box}:${entry.id}`}
                entry={entry}
                fixedEventId={eventId}
                current={location.pathname === path}
              />
            );
          })}
        </ul>
      )}
      {next !== null && (
        <div>
          <Button icon="chevron-down" variant="secondary" busy={busy} onClick={loadMore}>
            Mehr laden
          </Button>
        </div>
      )}
    </div>
  );
}

const NARROW = '(max-width: 899px)';

/**
 * Rahmen des Postfachs. Ab 900 px stehen Liste und Verlauf nebeneinander, darunter ist es je eine eigene Seite:
 * Ist unter dem Postfach ein Verlauf (oder „Neue Nachricht“) geöffnet, zeigt das Handy nur ihn, sonst nur die Liste.
 * Der geöffnete Verlauf kommt als Kind-Route (`Outlet`); beim Wechsel wird er neu aufgebaut.
 * Am Handy wandert der Fokus mit: in den Verlauf auf dessen Überschrift, zurück auf die zuletzt geöffnete Zeile.
 */
export function MailboxShell({ header, eventId }: { header: ReactNode; eventId?: string }) {
  const outlet = useOutlet();
  const location = useLocation();
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const previous = useRef<{ path: string; open: boolean } | null>(null);
  const open = outlet !== null;

  useEffect(() => {
    const before = previous.current;
    previous.current = { path: location.pathname, open };
    if (before === null || before.path === location.pathname) return;
    if (typeof window.matchMedia !== 'function' || !window.matchMedia(NARROW).matches) return;
    if (open) {
      // Die Überschrift des Verlaufs wird erst nach dem Laden gezeichnet: kurz darauf warten
      const detail = detailRef.current;
      if (detail === null) return;
      const focusHeading = (): boolean => {
        const heading = detail.querySelector('h1');
        if (heading === null) return false;
        heading.tabIndex = -1;
        heading.focus();
        return true;
      };
      if (focusHeading()) return;
      const observer = new MutationObserver(() => {
        if (focusHeading()) observer.disconnect();
      });
      observer.observe(detail, { childList: true, subtree: true });
      const timer = window.setTimeout(() => {
        observer.disconnect();
      }, 3000);
      return () => {
        observer.disconnect();
        window.clearTimeout(timer);
      };
    }
    // Zurück in die Liste: die zuletzt geöffnete Zeile, sonst die Liste selbst
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLAnchorElement>('a.mailbox__row') ?? [],
    );
    const row = rows.find((link) => link.getAttribute('href') === before.path);
    if (row !== undefined) row.focus();
    else listRef.current?.focus();
  }, [location.pathname, open]);

  return (
    <div className={`mailbox-shell${open ? ' mailbox-shell--detail' : ''}`}>
      <div className="mailbox-shell__list stack stack--lg" ref={listRef} tabIndex={-1}>
        {header}
        <Mailbox eventId={eventId} />
      </div>
      <div className="mailbox-shell__detail" key={location.pathname} ref={detailRef}>
        {outlet ?? (
          <p className="mailbox-shell__empty muted">Wählen Sie links eine Nachricht aus.</p>
        )}
      </div>
    </div>
  );
}
